import React, { useMemo, useState, useEffect, Fragment } from 'react'
import { Flex } from 'reflexbox'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'

import { Title, SemiTitle, Text } from 'components/Typography'
import Breadcrumb from 'components/Breadcrumb'
import Card from 'components/Card'
import Loader from 'components/Loader'
import Button from 'components/Button'

import GiftCardConsumptionList from 'containers/GiftCardComsumptionList'

import { formatCurrency } from 'utils/formatters'
import { getGiftCardOrder, exportCSV } from 'services/giftCards'
import { translateGiftCardPaymentStatus } from 'utils/translators'
import { download } from 'helpers/download'
import { useUser } from 'context/user-context'

const GiftCardOrderDetail = () => {
  const { user } = useUser()
  const [isLoading, setIsLoading] = useState(false)
  const [orderData, setOrderData] = useState({})
  const [isDownloading, setIsDownloading] = useState(false)

  const { id: orderID } = useParams()

  const breadcrumbPath = useMemo(
    () => [
      {
        label: 'Cartões presente',
        link: `${user.company_name ? '' : '/staff'}/prepaid-purchase/gift-cards`,
      },
      {
        label: `Pedido ${orderID}`,
      },
    ],
    [orderID],
  )

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setIsLoading(true)
        const { data } = await getGiftCardOrder(orderID)
        setOrderData(data)
      } catch (e) {
        console.log(e)
        toast.error('Aconteceu um erro ao resgatar os dados do pedido, por favor tente novamente.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchOrder()
  }, [])

  const getCsv = async () => {
    try {
      setIsDownloading(true)
      const { data } = await exportCSV(orderID)
      download(data, 'guigo_gift_cards')
    } catch (e) {
      console.log(e)
      toast.error('Aconteceu um erro ao export o arquivo CSV, por favor tente novamente.')
    } finally {
      setIsDownloading(false)
    }
  }

  const isStaff = user.company_name === undefined

  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Title as='h3'>Pedido {orderID}</Title>

      <Card display='flex' flexDirection='column' mt={24} mb={32}>
        {isLoading ? (
          <Loader />
        ) : (
          <Flex flexWrap='wrap'>
            <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
              <SemiTitle color='grays.N500'>Descrição</SemiTitle>
              <Text>{orderData?.description}</Text>
            </Flex>

            <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
              <SemiTitle color='grays.N500'>Valor total do pedido</SemiTitle>
              <Text>{formatCurrency(orderData?.amount)}</Text>
            </Flex>

            <Flex flexDirection='column' mb={16} flex={1} flexBasis={isStaff ? '50%' : '100%'}>
              <SemiTitle color='grays.N500'>Status de pagamento</SemiTitle>
              <Text>{translateGiftCardPaymentStatus(orderData?.transaction_status)}</Text>
            </Flex>

            {isStaff && (
              <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
                <SemiTitle color='grays.N500'>Data de geração</SemiTitle>
                <Text>{orderData?.created_at ? moment(orderData?.created_at).format('DD/MM/YYYY') : '-'}</Text>
              </Flex>
            )}

            <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
              <SemiTitle color='grays.N500'>Boleto</SemiTitle>
              <Text
                as='a'
                href={orderData?.transactions?.[0]?.boletos?.[0]?.payment_url}
                target='_blank'
                color='turquoises.T300'
                textDecoration='underline'
              >
                Visualizar boleto
              </Text>
            </Flex>

            <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
              <SemiTitle color='grays.N500'>Recibo</SemiTitle>
              <Text
                as='a'
                href={orderData?.transactions?.[0]?.boletos?.[0]?.receipt}
                target='_blank'
                color='turquoises.T300'
                textDecoration='underline'
              >
                Visualizar recibo
              </Text>
            </Flex>

            {orderData?.items?.map(item => (
              <Fragment key={item?.id}>
                <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
                  <SemiTitle color='grays.N500'>Cartões utilizados - {item.description}</SemiTitle>
                  <Text>
                    {Number(item?.used_gift_cards_field)}/{Number(item?.quantity)}
                  </Text>
                </Flex>
                <Flex flexDirection='column' mb={16} flex={1} flexBasis='50%'>
                  <SemiTitle color='grays.N500'>Valido de - Valido até</SemiTitle>
                  <Text>
                    {item.initial_date ? moment(item?.initial_date).format('DD/MM/YYYY') : ''} -&nbsp;
                    {item.expiration_date ? moment(item?.expiration_date).format('DD/MM/YYYY') : ''}
                  </Text>
                </Flex>
              </Fragment>
            ))}
          </Flex>
        )}
      </Card>

      <Flex alignItems='center' justifyContent='space-between'>
        <Title as='h3'>Histórico de consumo</Title>
        <Button size='big' onClick={getCsv} disabled={isDownloading}>
          {isDownloading ? <Loader /> : 'Download CSV'}
        </Button>
      </Flex>

      <GiftCardConsumptionList />
    </Flex>
  )
}

export default GiftCardOrderDetail
